import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import { Volume2, RotateCcw, Star, Trophy, PlayCircle, Sparkles, Home, Settings, Info, Target, Shuffle } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants matching other games
const SCORING_CONSTANTS = {
  BASE_POINTS: 20,
  STREAK_MULTIPLIER: 2,
  SPEED_BONUS_MAX: 30,
  ACCURACY_BONUS_MAX: 50,
  PERFECT_ACCURACY_BONUS: 100,
  LEVEL_COMPLETE_BONUS: 50,
  MAX_SPEED_BONUS_TIME: 30, // seconds per letter
  MIN_ACCURACY_FOR_STARS: 70
};

// Enhanced letter configuration with levels
const LETTERS_BY_LEVEL = {
  1: [
    {
      char: 'ا',
      name: 'Alif',
      nameArabic: 'أَلِف',
      svgFile: '/letters/alif.svg',
      nameSound: '/sounds/letters/name/alif.mp3',
      letterSound: '/sounds/letters/sound/alif.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 65, extra: 250 },
      difficulty: 1,
      description: 'The first letter - a simple vertical line',
      hints: [
        'Start from the top',
        'Draw straight down',
        'Keep the line vertical'
      ]
    },
    {
      char: 'د',
      name: 'Dal',
      nameArabic: 'دَال',
      svgFile: '/letters/dal.svg',
      nameSound: '/sounds/letters/name/dal.mp3',
      letterSound: '/sounds/letters/sound/dal.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 300 },
      difficulty: 3,
      description: 'A vertical line with a small curve at top',
      hints: [
        'Start with small horizontal line',
        'Draw straight down',
        'Keep it simple and clean'
      ]
    },
    {
      char: 'ر',
      name: 'Ra',
      nameArabic: 'رَاء',
      svgFile: '/letters/ra.svg',
      nameSound: '/sounds/letters/name/ra.mp3',
      letterSound: '/sounds/letters/sound/ra.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 300 },
      difficulty: 3,
      description: 'A small curve going down',
      hints: [
        'Start from the right',
        'Make a small curve',
        'Short and simple'
      ]
    }
  ],
  
  2: [
    {
      char: 'ب',
      name: 'Ba',
      nameArabic: 'بَاء',
      svgFile: '/letters/ba.svg',
      nameSound: '/sounds/letters/name/ba.mp3',
      letterSound: '/sounds/letters/sound/ba.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 300 },
      difficulty: 2,
      description: 'A curved line with one dot below',
      hints: [
        'Start from the right side',
        'Draw a gentle curve to the left',
        'Add the dot below in the center'
      ]
    },
    {
      char: 'ت',
      name: 'Ta',
      nameArabic: 'تَاء',
      svgFile: '/letters/ta.svg',
      nameSound: '/sounds/letters/name/ta.mp3',
      letterSound: '/sounds/letters/sound/ta.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 300 },
      difficulty: 3,
      description: 'Like Ba but with two dots above',
      hints: [
        'Draw the same base as Ba',
        'Add two dots above the line',
        'Space the dots evenly'
      ]
    },
    {
      char: 'ث',
      name: 'Tha',
      nameArabic: 'ثَاء',
      svgFile: '/letters/tha.svg',
      nameSound: '/sounds/letters/name/tha.mp3',
      letterSound: '/sounds/letters/sound/tha.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 350 },
      difficulty: 4,
      description: 'Like Ba but with three dots above',
      hints: [
        'Draw the same base as Ba',
        'Add three dots above the line',
        'Keep dots aligned and evenly spaced'
      ]
    }
  ],

  3: [
    {
      char: 'ذ',
      name: 'Thal',
      nameArabic: 'ذَال',
      svgFile: '/letters/thaal.svg',
      nameSound: '/sounds/letters/name/thaal.mp3',
      letterSound: '/sounds/letters/sound/thaal.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 300 },
      difficulty: 4,
      description: 'Like Dal but with one dot above',
      hints: [
        'Draw the same shape as Dal',
        'Add one dot above the top',
        'Keep dot centered'
      ]
    },
    {
      char: 'ز',
      name: 'Zay',
      nameArabic: 'زَاي',
      svgFile: '/letters/zay.svg',
      nameSound: '/sounds/letters/name/zay.mp3',
      letterSound: '/sounds/letters/sound/zay.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 300 },
      difficulty: 4,
      description: 'Like Ra but with one dot above',
      hints: [
        'Draw the same curve as Ra',
        'Add one dot above',
        'Keep it small and neat'
      ]
    },
    {
      char: 'ل',
      name: 'Lam',
      nameArabic: 'لاَم',
      svgFile: '/letters/lam.svg',
      nameSound: '/sounds/letters/name/lam.mp3',
      letterSound: '/sounds/letters/sound/lam.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 60, extra: 350 },
      difficulty: 4,
      description: 'A tall line that curves at the bottom',
      hints: [
        'Start from the top',
        'Draw tall vertical line',
        'Curve gently at the bottom'
      ]
    }
  ],

  4: [
    {
      char: 'ج',
      name: 'Jiim',
      nameArabic: 'جِيم',
      svgFile: '/letters/jiim.svg',
      nameSound: '/sounds/letters/name/jiim.mp3',
      letterSound: '/sounds/letters/sound/jiim.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 400 },
      difficulty: 5,
      description: 'A hook shape with a dot below',
      hints: [
        'Start from the top right',
        'Curve down and hook back',
        'Add one dot below the hook'
      ]
    },
    {
      char: 'ح',
      name: 'Ha',
      nameArabic: 'حَاء',
      svgFile: '/letters/ha.svg',
      nameSound: '/sounds/letters/name/ha.mp3',
      letterSound: '/sounds/letters/sound/ha.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 400 },
      difficulty: 5,
      description: 'Like Jiim but without the dot',
      hints: [
        'Same hook shape as Jiim',
        'No dot needed',
        'Keep the curve smooth'
      ]
    },
    {
      char: 'خ',
      name: 'Kha',
      nameArabic: 'خَاء',
      svgFile: '/letters/kha.svg',
      nameSound: '/sounds/letters/name/kha.mp3',
      letterSound: '/sounds/letters/sound/kha.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 400 },
      difficulty: 6,
      description: 'Like Ha but with one dot above',
      hints: [
        'Draw the hook like Ha',
        'Add one dot above the curve',
        'Position dot in the center'
      ]
    }
  ],

  5: [
    {
      char: 'و',
      name: 'Waw',
      nameArabic: 'وَاو',
      svgFile: '/letters/waw.svg',
      nameSound: '/sounds/letters/name/waw.mp3',
      letterSound: '/sounds/letters/sound/waw.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 350 },
      difficulty: 5,
      description: 'A small circle with a vertical line',
      hints: [
        'Draw small circle at top',
        'Add vertical line going down',
        'Keep circle small and neat'
      ]
    },
    {
      char: 'ن',
      name: 'Nuun',
      nameArabic: 'نُون',
      svgFile: '/letters/nuun.svg',
      nameSound: '/sounds/letters/name/nuun.mp3',
      letterSound: '/sounds/letters/sound/nuun.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 400 },
      difficulty: 6,
      description: 'A curved line with one dot above',
      hints: [
        'Draw a U-shaped curve',
        'Add one dot above the center',
        'Make curve smooth and even'
      ]
    }
  ],

  6: [
    {
      char: 'ط',
      name: 'Taa',
      nameArabic: 'طَاء',
      svgFile: '/letters/taa.svg',
      nameSound: '/sounds/letters/name/taa.mp3',
      letterSound: '/sounds/letters/sound/taa.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 400 },
      difficulty: 7,
      description: 'A horizontal line with a vertical line',
      hints: [
        'Draw horizontal line first',
        'Add vertical line in middle',
        'Keep lines straight'
      ]
    },
    {
      char: 'ظ',
      name: 'Thaa',
      nameArabic: 'ظَاء',
      svgFile: '/letters/thaa.svg',
      nameSound: '/sounds/letters/name/thaa.mp3',
      letterSound: '/sounds/letters/sound/thaa.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 55, extra: 450 },
      difficulty: 8,
      description: 'Like Taa but with one dot above',
      hints: [
        'Draw the same shape as Taa',
        'Add one dot above',
        'Keep everything aligned'
      ]
    },
    {
      char: 'ف',
      name: 'Fa',
      nameArabic: 'فَاء',
      svgFile: '/letters/fa.svg',
      nameSound: '/sounds/letters/name/fa.mp3',
      letterSound: '/sounds/letters/sound/fa.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 400 },
      difficulty: 7,
      description: 'A circle with a vertical line and dot above',
      hints: [
        'Draw a small circle',
        'Add vertical line below',
        'Place one dot above the circle'
      ]
    }
  ],

  7: [
    {
      char: 'س',
      name: 'Siin',
      nameArabic: 'سِين',
      svgFile: '/letters/siin.svg',
      nameSound: '/sounds/letters/name/siin.mp3',
      letterSound: '/sounds/letters/sound/siin.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 400 },
      difficulty: 7,
      description: 'Three small humps in a line',
      hints: [
        'Draw three connected bumps',
        'Start from the right',
        'Make bumps similar size'
      ]
    },
    {
      char: 'ش',
      name: 'Shiin',
      nameArabic: 'شِين',
      svgFile: '/letters/shiin.svg',
      nameSound: '/sounds/letters/name/shiin.mp3',
      letterSound: '/sounds/letters/sound/shiin.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 450 },
      difficulty: 8,
      description: 'Like Siin but with three dots above',
      hints: [
        'Draw the three humps like Siin',
        'Add three dots above',
        'Align dots with the humps'
      ]
    },
    {
      char: 'م',
      name: 'Miim',
      nameArabic: 'مِيم',
      svgFile: '/letters/miim.svg',
      nameSound: '/sounds/letters/name/miim.mp3',
      letterSound: '/sounds/letters/sound/miim.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 400 },
      difficulty: 7,
      description: 'A circular shape with a small tail',
      hints: [
        'Start with a circle',
        'Add small tail at bottom',
        'Keep the circle round'
      ]
    }
  ],

  8: [
    {
      char: 'ص',
      name: 'Saad',
      nameArabic: 'صَاد',
      svgFile: '/letters/saad.svg',
      nameSound: '/sounds/letters/name/saad.mp3',
      letterSound: '/sounds/letters/sound/saad.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 400 },
      difficulty: 8,
      description: 'A large curve with a vertical line',
      hints: [
        'Start with a big curve',
        'Add vertical line at the end',
        'Make it wide and bold'
      ]
    },
    {
      char: 'ض',
      name: 'Daad',
      nameArabic: 'ضَاد',
      svgFile: '/letters/daad.svg',
      nameSound: '/sounds/letters/name/daad.mp3',
      letterSound: '/sounds/letters/sound/daad.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 450 },
      difficulty: 9,
      description: 'Like Saad but with one dot above',
      hints: [
        'Draw the same shape as Saad',
        'Add one dot above the curve',
        'Position dot carefully'
      ]
    }
  ],

  9: [
    {
      char: 'ع',
      name: 'Ayn',
      nameArabic: 'عَيْن',
      svgFile: '/letters/ayn.svg',
      nameSound: '/sounds/letters/name/ayn.mp3',
      letterSound: '/sounds/letters/sound/ayn.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 400 },
      difficulty: 8,
      description: 'A circular shape with an opening',
      hints: [
        'Start from the top',
        'Make a C-like curve',
        'Leave opening at the top'
      ]
    },
    {
      char: 'غ',
      name: 'Ghayn',
      nameArabic: 'غَيْن',
      svgFile: '/letters/ghayn.svg',
      nameSound: '/sounds/letters/name/ghayn.mp3',
      letterSound: '/sounds/letters/sound/ghayn.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 450 },
      difficulty: 9,
      description: 'Like Ayn but with one dot above',
      hints: [
        'Draw the same curve as Ayn',
        'Add one dot above the opening',
        'Position dot at the top'
      ]
    },
    {
      char: 'ه',
      name: 'Ha (regular)',
      nameArabic: 'هَاء',
      svgFile: '/letters/haa.svg',
      nameSound: '/sounds/letters/name/haa.mp3',
      letterSound: '/sounds/letters/sound/haa.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 400 },
      difficulty: 7,
      description: 'An oval shape with an opening',
      hints: [
        'Draw an oval with gap',
        'Leave opening on the left',
        'Keep shape round'
      ]
    }
  ],

  10: [
    {
      char: 'ق',
      name: 'Qaf',
      nameArabic: 'قَاف',
      svgFile: '/letters/qaf.svg',
      nameSound: '/sounds/letters/name/qaf.mp3',
      letterSound: '/sounds/letters/sound/qaf.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 450 },
      difficulty: 8,
      description: 'Like Fa but with two dots above',
      hints: [
        'Draw the same shape as Fa',
        'Add two dots above the circle',
        'Space dots evenly'
      ]
    },
    {
      char: 'ك',
      name: 'Kaf',
      nameArabic: 'كَاف',
      svgFile: '/letters/kaf.svg',
      nameSound: '/sounds/letters/name/kaf.mp3',
      letterSound: '/sounds/letters/sound/kaf.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 45, extra: 450 },
      difficulty: 9,
      description: 'A complex shape with angles',
      hints: [
        'Start from the top right',
        'Make angular shapes',
        'Practice the angles carefully'
      ]
    },
    {
      char: 'ي',
      name: 'Ya',
      nameArabic: 'يَاء',
      svgFile: '/letters/ya.svg',
      nameSound: '/sounds/letters/name/ya.mp3',
      letterSound: '/sounds/letters/sound/ya.mp3',
      strokeWidth: 4,
      userStrokeWidth: 18,
      tolerance: { match: 50, extra: 450 },
      difficulty: 8,
      description: 'A curved line with two dots below',
      hints: [
        'Draw gentle curve from right to left',
        'Add two dots below at the end',
        'Make curve smooth and flowing'
      ]
    }
  ]
};

// Level configurations
const LEVEL_CONFIGS = {
  1: { 
    title: "Simple Lines",
    description: "Master basic vertical lines and curves!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[1],
    theme: "Start with the easiest letters - straight lines and simple curves"
  },
  2: { 
    title: "Dotted Family",
    description: "Learn the Ba family with dots!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[2],
    theme: "Master Ba, Ta, and Tha - similar shapes with different dots"
  },
  3: { 
    title: "More Dots",
    description: "Practice dotted variations!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[3],
    theme: "Learn how dots change letter sounds - Dal/Thal, Ra/Zay"
  },
  4: { 
    title: "Hook Shapes",
    description: "Master the hook family!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[4],
    theme: "Practice Jiim, Ha, and Kha - curved hook shapes"
  },
  5: { 
    title: "Circles & Curves",
    description: "Round shapes and smooth curves!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[5],
    theme: "Work with circular and curved letter forms"
  },
  6: { 
    title: "Complex Shapes",
    description: "Challenge yourself with harder letters!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[6],
    theme: "Practice more complex letter structures"
  },
  7: { 
    title: "Connected Forms",
    description: "Letters with multiple parts!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[7],
    theme: "Master letters with humps and connected elements"
  },
  8: { 
    title: "Wide Letters",
    description: "Big, bold letter shapes!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[8],
    theme: "Practice wide letters like Saad and Daad"
  },
  9: { 
    title: "Open Shapes",
    description: "Letters with openings and gaps!",
    passingScore: 80,
    letters: LETTERS_BY_LEVEL[9],
    theme: "Master letters with circular openings"
  },
  10: { 
    title: "Advanced Masters",
    description: "The most challenging letters!",
    passingScore: 85,
    letters: LETTERS_BY_LEVEL[10],
    theme: "Complete your Arabic alphabet mastery!"
  }
};

const CANVAS_WIDTH = 300;
const CANVAS_HEIGHT = 350;

const EnhancedArabicTracingGame = () => {
  // Firebase integration for progress tracking
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    bestScores,
    isLoading,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useFirebase('alphabetTracing');

  // Audio integration
  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  const { saveSessionData } = useAnalytics();

  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [svgPaths, setSvgPaths] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [correctLetters, setCorrectLetters] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [levelStartTime, setLevelStartTime] = useState(Date.now());
  const [letterStartTime, setLetterStartTime] = useState(Date.now());
  const [speedBonuses, setSpeedBonuses] = useState(0);
  const [streakBonuses, setStreakBonuses] = useState(0);
  const [stars, setStars] = useState(0);
  const [attempts, setAttempts] = useState(0);
  
  // Audio state
  const [playingSound, setPlayingSound] = useState(null);
  const [audioError, setAudioError] = useState(null);
  
  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [showHints, setShowHints] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [ghostOpacity, setGhostOpacity] = useState(0.3);
  const [feedback, setFeedback] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  
  // Celebration state
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);

  // Refs
  const canvasRef = useRef();
  const guideCanvasRef = useRef();
  const userCanvasRef = useRef();
  const soundRef = useRef(null);

  // Get current level config and letters
  const levelConfig = useMemo(() => LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1], [level]);
  const currentLetter = useMemo(() => {
    const letters = levelConfig.letters || [];
    return letters[currentLetterIndex] || letters[0];
  }, [levelConfig, currentLetterIndex]);

  // Generate fallback paths for missing SVGs
  const generateFallbackPath = useCallback((char) => {
    const fallbacks = {
      'ا': 'M150 50 L150 250',
      'ب': 'M50 180 Q100 160 150 160 Q200 160 250 180',
      'ت': 'M50 180 Q100 160 150 160 Q200 160 250 180',
      'ث': 'M50 180 Q100 160 150 160 Q200 160 250 180',
      'ج': 'M80 120 Q120 100 160 100 Q200 100 220 120 Q220 160 200 180 Q180 200 140 200 Q120 200 110 190'
    };
    return fallbacks[char] || 'M150 50 L150 250';
  }, []);

  // Load SVG paths with error handling
  const loadSvgPaths = useCallback(async () => {
    const paths = {};
    const allLetters = Object.values(LEVEL_CONFIGS).flatMap(config => config.letters);
    
    const loadPromises = allLetters.map(async (letter) => {
      try {
        const response = await fetch(letter.svgFile);
        if (!response.ok) {
          throw new Error(`Failed to load ${letter.svgFile}: ${response.status}`);
        }
        
        const svgText = await response.text();
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
        
        const parserError = svgDoc.querySelector('parsererror');
        if (parserError) {
          throw new Error(`SVG parsing error for ${letter.svgFile}`);
        }
        
        const path = svgDoc.querySelector('path');
        if (path) {
          paths[letter.char] = path.getAttribute('d');
        } else {
          console.warn(`No path found in ${letter.svgFile}`);
          paths[letter.char] = generateFallbackPath(letter.char);
        }
      } catch (error) {
        console.error(`Error loading SVG for ${letter.name}:`, error);
        paths[letter.char] = generateFallbackPath(letter.char);
      }
    });

    await Promise.all(loadPromises);
    setSvgPaths(paths);
  }, [generateFallbackPath]);

  // Audio playback with error handling
  const playSound = useCallback(async (type) => {
    if (soundRef.current) {
      soundRef.current.pause();
      soundRef.current.currentTime = 0;
      soundRef.current = null;
    }
    
    const soundFile = type === 'name' ? currentLetter.nameSound : currentLetter.letterSound;
    if (!soundFile) {
      setAudioError('Sound file not available');
      return;
    }

    setPlayingSound(type);
    setAudioError(null);
    
    try {
      const audio = new Audio(soundFile);
      soundRef.current = audio;
      
      audio.addEventListener('loadeddata', () => {
        audio.play().catch(error => {
          console.error('Audio playback failed:', error);
          setAudioError('Audio playback failed');
          setPlayingSound(null);
        });
      });
      
      audio.addEventListener('ended', () => {
        setPlayingSound(null);
      });
      
      audio.addEventListener('error', (error) => {
        console.error('Audio loading failed:', error);
        setAudioError('Audio file not found');
        setPlayingSound(null);
      });
      
    } catch (error) {
      console.error('Failed to create audio:', error);
      setAudioError('Failed to play audio');
      setPlayingSound(null);
    }
  }, [currentLetter]);

  // Scoring calculations
  const calculateStars = useCallback((accuracy, totalScore, totalLetters) => {
    const maxPossibleScore = totalLetters * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.SPEED_BONUS_MAX);
    const scorePercentage = (totalScore / maxPossibleScore) * 100;
    
    if (accuracy >= 95 && scorePercentage >= 85) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 70 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  const calculateSpeedBonus = useCallback((responseTime) => {
    const maxTime = SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME * 1000;
    if (responseTime <= maxTime) {
      const bonus = Math.round(SCORING_CONSTANTS.SPEED_BONUS_MAX * (1 - responseTime / maxTime));
      return Math.max(0, bonus);
    }
    return 0;
  }, []);

  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0) => {
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) {
      return;
    }
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#f59e0b', '#d97706', '#92400e'];
    let particleCount = 30;
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      shapes = ['star'];
      particleCount = 50;
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500'];
      particleCount = 40;
      playStreakSound();
    } else {
      playCorrectSound();
    }
    
    confetti({
      particleCount,
      spread: 50,
      origin: { y: 0.7 },
      colors,
      shapes,
      gravity: 1.2,
      drift: 0
    });

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1500);
  }, [playStarSound, playStreakSound, playCorrectSound]);

  // Canvas drawing functions
  const drawPathToCanvas = useCallback((ctx, path, strokeWidth, strokeColor = 'black') => {
    if (!path) return;
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    try {
      const p = new Path2D(path);
      ctx.stroke(p);
    } catch (error) {
      console.error('Error drawing path:', error);
    }
  }, []);

  const drawUserPaths = useCallback((ctx, paths, strokeWidth, strokeColor = 'blue') => {
    if (!paths || paths.length === 0) return;
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    paths.forEach(path => {
      if (path.paths && path.paths.length > 0) {
        ctx.beginPath();
        ctx.moveTo(path.paths[0].x, path.paths[0].y);
        for (let i = 1; i < path.paths.length; i++) {
          ctx.lineTo(path.paths[i].x, path.paths[i].y);
        }
        ctx.stroke();
      }
    });
  }, []);

  // Advanced stroke analysis
  const analyzeTracing = useCallback(async () => {
    setIsAnalyzing(true);
    
    try {
      const paths = await canvasRef.current?.exportPaths();
      if (!paths || paths.length === 0) {
        setFeedback("Please trace the letter first");
        setIsAnalyzing(false);
        return;
      }

      const guideCtx = guideCanvasRef.current?.getContext('2d');
      const userCtx = userCanvasRef.current?.getContext('2d');

      if (!guideCtx || !userCtx || !svgPaths[currentLetter.char]) {
        setFeedback("Error analyzing tracing");
        setIsAnalyzing(false);
        return;
      }

      drawPathToCanvas(guideCtx, svgPaths[currentLetter.char], currentLetter.strokeWidth);
      drawUserPaths(userCtx, paths, currentLetter.userStrokeWidth);

      const guideData = guideCtx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT).data;
      const userData = userCtx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT).data;

      let overlap = 0;
      let guidePixels = 0;
      let extraPixels = 0;

      for (let i = 0; i < guideData.length; i += 4) {
        const guideAlpha = guideData[i + 3];
        const userAlpha = userData[i + 3];
        
        if (guideAlpha > 0) {
          guidePixels++;
          if (userAlpha > 0) {
            overlap++;
          }
        } else if (userAlpha > 0) {
          extraPixels++;
        }
      }

      const matchPercent = guidePixels > 0 ? (overlap / guidePixels) * 100 : 0;
      const extraPercent = guidePixels > 0 ? (extraPixels / guidePixels) * 100 : 0;
      
      console.log(`Analysis: Match: ${matchPercent.toFixed(2)}% | Extra: ${extraPercent.toFixed(2)}%`);

      const isSuccess = matchPercent >= currentLetter.tolerance.match && 
                       extraPercent <= currentLetter.tolerance.extra;

      setAttempts(prev => prev + 1);

      if (isSuccess) {
        handleSuccess(matchPercent);
      } else {
        handleFailure(matchPercent, extraPercent);
      }
      
    } catch (error) {
      console.error('Error in tracing analysis:', error);
      setFeedback("Error analyzing your tracing. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  }, [currentLetter, svgPaths, drawPathToCanvas, drawUserPaths]);

  // Handle successful tracing
  const handleSuccess = useCallback((accuracy) => {
    const responseTime = Date.now() - letterStartTime;
    const speedBonus = calculateSpeedBonus(responseTime);
    const newStreak = currentStreak + 1;
    
    let points = SCORING_CONSTANTS.BASE_POINTS;
    if (newStreak >= 3) {
      points *= SCORING_CONSTANTS.STREAK_MULTIPLIER;
      setStreakBonuses(prev => prev + (SCORING_CONSTANTS.BASE_POINTS * (SCORING_CONSTANTS.STREAK_MULTIPLIER - 1)));
    }
    
    setCorrectLetters(prev => prev + 1);
    setCurrentStreak(newStreak);
    setBestStreak(prev => Math.max(prev, newStreak));
    setTotalScore(prev => prev + points + speedBonus);
    setSpeedBonuses(prev => prev + speedBonus);
    
    setIsComplete(true);
    setFeedback(`Excellent! ${Math.round(accuracy)}% accuracy!`);
    
    const isStreak = newStreak >= 3;
    triggerCelebration(isStreak);
    
    // Check if level complete
    const newCorrectCount = correctLetters + 1;
    if (newCorrectCount >= levelConfig.letters.length) {
      setTimeout(() => {
        handleLevelComplete(newCorrectCount);
      }, 2000);
    } else {
      // Auto-move to next letter after celebration
      setTimeout(() => {
        handleNextLetter();
      }, 3000);
    }
  }, [
    letterStartTime,
    currentStreak,
    correctLetters,
    levelConfig.letters.length,
    calculateSpeedBonus,
    triggerCelebration
  ]);

  // Handle failed tracing
  const handleFailure = useCallback((matchPercent, extraPercent) => {
    setSessionMistakes(prev => [...prev, {
      timestamp: Date.now(),
      type: 'incorrect_tracing',
      letter: currentLetter.char,
      letterName: currentLetter.name,
      letterIndex: currentLetterIndex,
      attempt: attempts + 1
    }]);
    setCurrentStreak(0);
    playIncorrectSound();
    
    let feedbackMessage = `Try again! ${Math.round(matchPercent)}% accuracy`;
    
    if (matchPercent < currentLetter.tolerance.match) {
      feedbackMessage += " - follow the gray line more closely";
    }
    if (extraPercent > currentLetter.tolerance.extra) {
      feedbackMessage += " - avoid drawing outside the letter";
    }
    
    setFeedback(feedbackMessage);
    
    setTimeout(() => {
      handleClear();
    }, 2000);
  }, [currentLetter.tolerance, playIncorrectSound]);

  // Handle level completion
  const handleLevelComplete = useCallback((finalCorrectLetters = null) => {
    const actualCorrectLetters = finalCorrectLetters !== null ? finalCorrectLetters : correctLetters;
    const totalLetters = levelConfig.letters.length;
    const accuracy = Math.round((actualCorrectLetters / totalLetters) * 100);
    const accuracyBonus = accuracy === 100 ? 
      SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS : 
      Math.round((accuracy / 100) * SCORING_CONSTANTS.ACCURACY_BONUS_MAX);
    
    const finalScore = totalScore + accuracyBonus + SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS;
    const earnedStars = calculateStars(accuracy, finalScore, totalLetters);
    
    const scoreData = {
      totalScore: finalScore,
      accuracy,
      stars: earnedStars,
      streak: bestStreak,
      speedBonuses,
      streakBonuses,
      accuracyBonus,
      levelCompleteBonus: SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS,
      completedAt: new Date().toISOString()
    };
    
    setStars(earnedStars);
    saveLevelData(level, scoreData);

    const sessionData = {
      gameType: 'alphabetTracing',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalScore,
      accuracy,
      attempts: levelConfig.letters.length,
      correctAnswers: actualCorrectLetters,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      speedBonuses,
      streakBonuses,
      levelTitle: levelConfig.title
    };
    saveSessionData(sessionData);
    
    const nextLevel = level + 1;
    const isLastLevel = !LEVEL_CONFIGS[nextLevel];
    const passedLevel = accuracy >= levelConfig.passingScore;
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      passedLevel,
      levelTitle: levelConfig.title,
      passingScore: levelConfig.passingScore
    });
    
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }
    
    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
    }
  }, [
    correctLetters,
    levelConfig,
    totalScore,
    bestStreak,
    speedBonuses,
    streakBonuses,
    level,
    calculateStars,
    saveLevelData,
    triggerCelebration,
    playPerfectSound,
    unlockNextLevel
  ]);

  // Navigation functions
  const handleNextLetter = useCallback(() => {
    const nextIndex = currentLetterIndex + 1;
    if (nextIndex < levelConfig.letters.length) {
      setCurrentLetterIndex(nextIndex);
      handleClear();
      setIsComplete(false);
      setLetterStartTime(Date.now());
      playWhooshSound();
    }
  }, [currentLetterIndex, levelConfig.letters.length, playWhooshSound]);

  const handleClear = useCallback(() => {
    canvasRef.current?.clearCanvas();
    setIsComplete(false);
    setFeedback('');
  }, []);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]);
    setGamePhase('playing');
    setCurrentLetterIndex(0);
    setTotalScore(0);
    setCorrectLetters(0);
    setCurrentStreak(0);
    setBestStreak(0);
    setSpeedBonuses(0);
    setStreakBonuses(0);
    setStars(0);
    setAttempts(0);
    setLevelStartTime(Date.now());
    setLetterStartTime(Date.now());
    handleClear();
  }, [handleClear]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
    handleClear();
  }, [handleClear]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    updateLevel(1);
    setGamePhase('menu');
    setShowResetConfirmation(false);
  }, [resetAllProgress, updateLevel]);

  const handleProceedToNextLevel = useCallback(() => {
    if (levelCompleteData?.passedLevel && !levelCompleteData?.isLastLevel) {
      updateLevel(levelCompleteData.nextLevel);
    }
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const selectLevel = useCallback((newLevel) => {
    setLevel(newLevel);
  }, [setLevel]);

  // Initialize component
  useEffect(() => {
    loadSvgPaths();
    
    const timer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    
    return () => {
      clearTimeout(timer);
      if (soundRef.current) {
        soundRef.current.pause();
        soundRef.current = null;
      }
    };
  }, [loadSvgPaths, startBackgroundMusic]);

  // Components
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => (
    <div className={`flex justify-center gap-1 ${size}`}>
      {[1, 2, 3].map(star => (
        <span 
          key={star} 
          className={`drop-shadow-lg transition-all duration-300 ${star <= count ? 'text-yellow-400 animate-pulse' : 'text-gray-300'}`}
        >
          ⭐
        </span>
      ))}
    </div>
  ));

  StarDisplay.propTypes = {
    count: PropTypes.number.isRequired,
    size: PropTypes.string
  };

  // Loading state
  if (isLoading || Object.keys(svgPaths).length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">✏️</div>
            <div className="text-orange-600 text-xl md:text-3xl font-bold animate-pulse">Loading Trace Quest...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Preparing your writing adventure!</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            ✏️ Trace Quest ✏️
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Master Arabic letters through guided tracing!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-orange-600">
                🎮 Level {level}: {levelConfig.title}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                {levelConfig.description}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">✏️</div>
                  <p className="font-bold text-sm md:text-lg">Letters</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.letters.length}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Accuracy Goal</p>
                  <p className="text-lg md:text-2xl font-black">75%+</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white col-span-2 md:col-span-1">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.passingScore}%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  ✏️ START TRACING! ✏️
                </button>
              </div>
            </div>

            {/* Level Grid */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-orange-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                {Object.entries(LEVEL_CONFIGS).map(([levelNum, config]) => {
                  const num = parseInt(levelNum);
                  const isUnlocked = num <= maxLevel;
                  const isCurrent = num === level;
                  const levelScore = bestScores[`level${num}`];
                  
                  return (
                    <button
                      key={num}
                      onClick={() => isUnlocked ? selectLevel(num) : null}
                      disabled={!isUnlocked}
                      className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${isCurrent 
                          ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                          : isUnlocked 
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                        }
                      `}
                      title={isUnlocked ? config.title : 'Locked'}
                    >
                      <div className="text-lg md:text-2xl font-black">{num}</div>
                      <div className="text-xs mt-1 font-bold text-center">{config.letters.length} letters</div>
                      {levelScore && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl md:text-3xl">🔒</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                  <button 
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    🔙 Main Menu
                  </button>
                </Link>
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Trace
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Progress and Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl md:text-2xl font-bold text-orange-600">
                  Letter {currentLetterIndex + 1} of {levelConfig.letters.length}: {currentLetter?.name}
                </h3>
                <div className="text-right">
                  <p className="text-lg font-bold text-indigo-600">{totalScore} points</p>
                  <p className="text-sm text-gray-600">{correctLetters} complete</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${((currentLetterIndex + (isComplete ? 1 : 0)) / levelConfig.letters.length) * 100}%` }}
                ></div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{totalScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctLetters}/{levelConfig.letters.length}</p>
                  <p className="text-xs font-bold">Complete</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔥</div>
                  <p className="font-bold text-lg md:text-xl">{currentStreak}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🏆</div>
                  <p className="font-bold text-lg md:text-xl">{bestStreak}</p>
                  <p className="text-xs font-bold">Best</p>
                </div>
              </div>
              
              {currentStreak >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {currentStreak} in a row! Perfect tracing! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Main Game Area */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-6 border-4 border-yellow-300">
              {/* Current Letter Display */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-8 mb-4">
                  <div className="text-center">
                    <div className="text-6xl md:text-8xl font-bold text-orange-600 mb-2">
                      {currentLetter?.char}
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-orange-800">
                      {currentLetter?.name}
                    </h2>
                    <p className="text-lg text-orange-600">
                      {currentLetter?.nameArabic}
                    </p>
                  </div>
                  
                  {/* Audio controls */}
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => playSound('name')}
                      disabled={playingSound === 'name'}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                    >
                      <Volume2 size={16} />
                      <span className="text-sm">Name</span>
                      {playingSound === 'name' && (
                        <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                      )}
                    </button>
                    
                    <button
                      onClick={() => playSound('sound')}
                      disabled={playingSound === 'sound'}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                    >
                      <PlayCircle size={16} />
                      <span className="text-sm">Sound</span>
                      {playingSound === 'sound' && (
                        <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                      )}
                    </button>
                  </div>
                </div>
                
                <p className="text-gray-600 text-lg font-medium mb-4">
                  {currentLetter?.description}
                </p>
                
                {/* Hints */}
                {showHints && currentLetter?.hints && (
                  <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-200 mb-6">
                    <h4 className="font-bold text-amber-800 mb-2">💡 Tracing Tips:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-amber-700">
                      {currentLetter.hints.map((hint, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-amber-500 font-bold">{index + 1}.</span>
                          {hint}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Feedback */}
              {feedback && (
                <div className={`text-center mb-6 p-4 rounded-2xl border-4 ${
                  feedback.includes('Excellent') || feedback.includes('Perfect')
                    ? 'text-green-600 bg-green-100 border-green-300' 
                    : feedback.includes('Try again')
                    ? 'text-red-600 bg-red-100 border-red-300'
                    : 'text-blue-600 bg-blue-100 border-blue-300'
                }`}>
                  <div className={`text-3xl mb-2 ${
                    feedback.includes('Excellent') ? 'animate-bounce' : 'animate-pulse'
                  }`}>
                    {feedback.includes('Excellent') ? '🎉' : feedback.includes('Try again') ? '❌' : 'ℹ️'}
                  </div>
                  <p className="text-xl font-bold">{feedback}</p>
                </div>
              )}

              {/* Canvas Container */}
              <div className="relative bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300 mb-6">
                {showGrid && (
                  <div className="absolute inset-0 opacity-20 pointer-events-none">
                    <svg width="100%" height="100%" className="absolute inset-0">
                      <defs>
                        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#888" strokeWidth="0.5"/>
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>
                  </div>
                )}

                {/* Ghost Trail SVG Overlay */}
                {svgPaths[currentLetter?.char] && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <svg
                      viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
                      width={CANVAS_WIDTH}
                      height={CANVAS_HEIGHT}
                      className="absolute"
                      style={{ opacity: ghostOpacity }}
                    >
                      <path
                        d={svgPaths[currentLetter.char]}
                        stroke="#e5e7eb"
                        strokeWidth={currentLetter.strokeWidth + 4}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d={svgPaths[currentLetter.char]}
                        stroke="#10b981"
                        strokeWidth={currentLetter.strokeWidth}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="8,4"
                      />
                    </svg>
                  </div>
                )}

                {/* Main Drawing Canvas */}
                <div className="flex justify-center">
                  <ReactSketchCanvas
                    ref={canvasRef}
                    strokeColor="#3b82f6"
                    strokeWidth={currentLetter?.userStrokeWidth || 18}
                    style={{
                      border: '2px solid #d1d5db',
                      borderRadius: '12px',
                      width: CANVAS_WIDTH,
                      height: CANVAS_HEIGHT,
                      backgroundColor: 'white'
                    }}
                    canvasColor="white"
                    allowOnlyPointerType="all"
                  />
                </div>

                {/* Hidden Analysis Canvases */}
                <canvas
                  ref={guideCanvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  style={{ display: 'none' }}
                />
                <canvas
                  ref={userCanvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleClear}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  <RotateCcw size={20} />
                  Clear
                </button>
                
                <button
                  onClick={analyzeTracing}
                  disabled={isComplete || isAnalyzing}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAnalyzing ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Checking...
                    </>
                  ) : (
                    <>
                      <span>✅</span>
                      Check Tracing
                    </>
                  )}
                </button>
                
                <button 
                  onClick={() => setShowSettings(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  <Settings size={20} />
                  Settings
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={startLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-orange-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-orange-600">✏️ How to Trace Letters!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Trace Arabic letters by following the gray dashed guide lines. Practice proper letter formation and improve your handwriting!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">✏️ How to Trace</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Look at the gray dashed guide line</li>
                    <li>Use your finger or stylus to trace over it</li>
                    <li>Follow the hints for proper stroke order</li>
                    <li>Stay within the letter boundaries</li>
                    <li>Click "Check Tracing" when finished</li>
                    <li>Aim for 75%+ accuracy to pass!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">🔊 Audio Help</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Click "Name" to hear the letter name in Arabic</li>
                    <li>Click "Sound" to hear the letter's phonetic sound</li>
                    <li>Listen while you trace to reinforce learning</li>
                    <li>Audio helps with pronunciation practice</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">🏆 Scoring System</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Earn points for accurate tracing</li>
                    <li>Get speed bonuses for quick completion</li>
                    <li>Build streaks of 3+ for double points</li>
                    <li>Stars based on overall performance</li>
                    <li>Level up by completing all letters!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                  <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Pro Tips</h3>
                  <ul className="list-disc list-inside text-red-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Take your time - accuracy is more important than speed</li>
                    <li>Use the settings to adjust guide visibility</li>
                    <li>Practice the same letter multiple times</li>
                    <li>Pay attention to stroke direction and order</li>
                    <li>Use a stylus or finger for better control</li>
                    <li>Listen to audio while tracing for better learning</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Start Tracing! ✏️
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border-4 border-orange-300">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-orange-800">⚙️ Tracing Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Ghost Opacity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guide Line Visibility
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={ghostOpacity}
                  onChange={(e) => setGhostOpacity(parseFloat(e.target.value))}
                  className="w-full accent-orange-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Hidden</span>
                  <span>Visible</span>
                </div>
              </div>

              {/* Show Hints Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Show Tracing Tips
                </label>
                <button
                  onClick={() => setShowHints(!showHints)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showHints ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showHints ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Show Grid Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Show Grid Lines
                </label>
                <button
                  onClick={() => setShowGrid(!showGrid)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showGrid ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showGrid ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setShowSettings(false)}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">
                {levelCompleteData.passedLevel ? '🎉' : '😊'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-orange-600 mb-2 md:mb-3">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </h3>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
                <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                  Final Score: {levelCompleteData.totalScore}
                </p>
                <p className="text-base md:text-lg text-gray-600 mt-2">
                  Accuracy: {levelCompleteData.accuracy}% • Best Streak: {levelCompleteData.streak}
                </p>
              </div>

              <div className="mb-6 md:mb-8">
                {levelCompleteData.passedLevel ? (
                  <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                    <p className="text-green-800 font-bold text-lg md:text-xl">
                      ✅ Fantastic! You passed! 🎉
                    </p>
                    <p className="text-green-700 text-base md:text-lg">
                      {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                    </p>
                    {!levelCompleteData.isLastLevel && (
                      <p className="text-green-700 text-base md:text-lg mt-2">Ready for the next level of letters!</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-orange-100 to-orange-200 border-2 md:border-4 border-orange-400 rounded-2xl p-4 md:p-6">
                    <p className="text-orange-800 font-bold text-lg md:text-xl">
                      😊 Good progress! Keep practicing!
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">
                      You need {levelCompleteData.passingScore}% accuracy to pass
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">Practice more to master these letters!</p>
                  </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Practice Again
                </button>
                
                {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                  <button 
                    onClick={handleProceedToNextLevel}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🚀 Next Level! 🚀
                  </button>
                )}
                
                {levelCompleteData.isLastLevel && (
                  <button 
                    onClick={() => { 
                      setShowLevelComplete(false); 
                      setGamePhase('menu'); 
                    }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🎊 You're a Tracing Master! 🎊
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Everything?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
                This will delete all your amazing tracing progress, scores, and stars. You'll start from Level 1 again.
                This cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedArabicTracingGame;